<template>
    <div class="flex w-full h-screen overflow-hidden bg-slate-100">
        <div class="flex-1 w-full overflow-hidden">
            <Topbar />
            <Toast />

            <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
                <RouterLink to="/store" class="mb-3 inline-flex w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900">
                    <i class="pi pi-arrow-left" />
                    Назад
                </RouterLink>

                <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
                    <div>
                        <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Цена реализации</h2>
                        <p class="mt-1 text-sm text-slate-500 md:text-base">Ежедневная настройка цены реализации по остаткам склада</p>
                    </div>
                </div>

                <div class="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
                <DataTable ref="storePriceTable" :value="filteredStores" editMode="cell" 
                    resizableColumns  
                    scrollable columnResizeMode="fit" showGridlines stripedRows size="small"
                    class="reference-table h-full"
                    scrollHeight="flex"
                >
                    <template #header>
                        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div class="flex w-full flex-wrap items-center gap-2">
                                <IconField class="w-full min-w-[16rem] flex-1 md:max-w-xl">
                                    <InputIcon><i class="pi pi-search" /></InputIcon>
                                    <InputText v-model.trim="searchQuery" class="w-full" placeholder="Поиск по названию, штрихкоду или закупочной цене..." />
                                </IconField>
                                <Select
                                    v-model="selectedClassificationFilter"
                                    :options="classificationOptions"
                                    placeholder="Все классы"
                                    showClear
                                    class="w-full sm:w-52"
                                />
                                <Select
                                    v-model="selectedCategoryFilter"
                                    :options="filteredCategoryOptions"
                                    placeholder="Все категории"
                                    showClear
                                    :disabled="!selectedClassificationFilter"
                                    class="w-full sm:w-52"
                                />
                                <Button
                                    type="button"
                                    icon="pi pi-filter-slash"
                                    label="Очистить"
                                    variant="outlined"
                                    severity="secondary"
                                    class="w-full sm:w-auto"
                                    @click="clearFilters()"
                                />
                            </div>

                            <div class="flex flex-nowrap items-center gap-2 right-0 lg:w-72">
                                <Button
                                    :label="`Без цены реализации (${missingRealizationPriceCount})`"
                                    icon="pi pi-tag"
                                    size="small"
                                    :severity="activeQuickFilter === 'missing-realization-price' ? 'danger' : 'secondary'"
                                    :outlined="activeQuickFilter !== 'missing-realization-price'"
                                    :class="activeQuickFilter === 'missing-realization-price' ? '!shadow-sm' : '!border-rose-200 !bg-rose-50 !text-rose-800 hover:!bg-rose-100'"
                                    @click="toggleQuickFilter('missing-realization-price')"
                                />
                            </div>
                        </div>
                    </template>
                    <Column field="name" header="Наименование" frozen style="min-width:150px" />
                    <Column field="barcode" header="Штрихкод" style="min-width:140px">
                        <template #body="{ data }">
                            {{ formatBarcodeValue(data.barcode) }}
                        </template>
                    </Column>
                    <Column field="class" header="Класс" style="min-width:120px" />
                    <Column field="category" header="Категория" style="min-width:120px" />
                    <Column field="quantity" header="Кол-во в остатке" style="min-width:130px" />
                    <Column field="base_price" header="Закуп. цена/ед." style="min-width:130px" />
                    
                    <Column v-for="(col, index) in dateColumns" :key="index" :header="col.label" :field="col.field">
                        <template #body="{ data }">
                            <InputNumber
                                v-if="col.isToday && editingId === data.id"
                                v-model="data.prices[col.field]"
                                style="width: 100px"
                                mode="decimal"
                                locale="ru-RU"
                                :useGrouping="false"
                                :minFractionDigits="0"
                                :maxFractionDigits="3"
                                fluid
                            />
                            <span
                                v-else
                                :class="col.isToday && hasMissingRealizationPrice(data) ? 'font-medium text-rose-700' : ''"
                            >
                                {{ formatPriceCell(data.prices[col.field], col.isToday) }}
                            </span>
                        </template>
                    </Column>
                    <Column header="Действие" frozen alignFrozen="right" style="min-width:120px">
                        <template #body="{ data }">
                            <div class="flex gap-2 justify-center">
                                <Button
                                  v-if="editingId !== data.id"
                                  icon="pi pi-pencil"
                                  severity="secondary"
                                  size="small"
                                  @click="startEdit(data.id)"
                                />
                                <Button
                                  v-else
                                  icon="pi pi-check"
                                  severity="success"
                                  size="small"
                                  @click="saveEdit(data)"
                                />
                                <Button
                                  v-if="editingId === data.id"
                                  icon="pi pi-times"
                                  severity="danger"
                                  size="small"
                                  @click="cancelEdit"
                                />
                            </div>
                        </template>
                    </Column>
                </DataTable>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import axios from '../lib/axios'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const toast = useToast()
const stores = ref([])
const dateColumns = ref([])
const storePriceTable = ref([])
const editingId = ref(null)
const originalPrice = ref(null)
const searchQuery = ref('')
const selectedClassificationFilter = ref(null)
const selectedCategoryFilter = ref(null)
const activeQuickFilter = ref('all')

const formatBarcodeValue = (value) => {
    if (value === null || value === undefined) return '—'
    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (item === null || item === undefined) return null
                if (typeof item === 'object') return item.barcode ?? null
                return item
            })
            .filter(Boolean)
            .join(', ') || '—'
    }
    if (typeof value === 'object') {
        if (Array.isArray(value.data)) {
            return value.data.map((item) => item?.barcode ?? item).filter(Boolean).join(', ') || '—'
        }
        if (value.barcode !== undefined) {
            return String(value.barcode)
        }
    }
    return String(value)
}

const todayField = computed(() => {
    return dateColumns.value.find((column) => column.isToday)?.field || null
})

const hasMissingRealizationPrice = (store) => {
    const field = todayField.value
    if (!field) return false

    const value = store?.prices?.[field]
    return value === null || value === undefined || value === ''
}

const isEditingStore = (store) => {
    return store?.id === editingId.value
}

const formatPriceCell = (value, isToday = false) => {
    if (value === null || value === undefined || value === '') {
        return isToday ? 'Не указана' : '—'
    }

    return `${Number(value).toLocaleString('ru-RU')} ₸`
}

const classificationOptions = computed(() => {
    return [...new Set(
        stores.value
            .map((store) => String(store.class || '').trim())
            .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'ru'))
})

const filteredCategoryOptions = computed(() => {
    const items = stores.value.filter((store) => {
        if (!selectedClassificationFilter.value) {
            return true
        }

        return String(store.class || '').trim() === selectedClassificationFilter.value
    })

    return [...new Set(
        items
            .map((store) => String(store.category || '').trim())
            .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'ru'))
})

const baseFilteredStores = computed(() => {
    const query = searchQuery.value.trim().toLowerCase()

    return stores.value.filter((store) => {
        const name = String(store.name || '').toLowerCase()
        const barcode = formatBarcodeValue(store.barcode).toLowerCase()
        const basePrice = String(store.base_price ?? '').toLowerCase()
        const classification = String(store.class || '').trim()
        const category = String(store.category || '').trim()
        const searchMatch = !query || name.includes(query) || barcode.includes(query) || basePrice.includes(query)
        const classificationMatch = !selectedClassificationFilter.value || classification === selectedClassificationFilter.value
        const categoryMatch = !selectedCategoryFilter.value || category === selectedCategoryFilter.value

        return searchMatch && classificationMatch && categoryMatch
    })
})

const missingRealizationPriceCount = computed(() => {
    return baseFilteredStores.value.filter((store) => hasMissingRealizationPrice(store) || isEditingStore(store)).length
})

const filteredStores = computed(() => {
    return baseFilteredStores.value.filter((store) => {
        if (activeQuickFilter.value === 'missing-realization-price') {
            return hasMissingRealizationPrice(store) || isEditingStore(store)
        }

        return true
    })
})

watch(selectedClassificationFilter, (nextValue, previousValue) => {
    if (nextValue !== previousValue) {
        selectedCategoryFilter.value = null
    }
})

const toggleQuickFilter = (filterName) => {
    activeQuickFilter.value = activeQuickFilter.value === filterName ? 'all' : filterName
}

const clearFilters = () => {
    searchQuery.value = ''
    selectedClassificationFilter.value = null
    selectedCategoryFilter.value = null
    activeQuickFilter.value = 'all'
}

const fetchData = async () => {
    try {
        const { data } = await axios.get('/api/weekly-store-prices')
        stores.value = Array.isArray(data) ? data : []

        const keys = Object.keys(stores.value[0]?.prices || {})
        const serverTodayField = keys[keys.length - 1] || null

        dateColumns.value = keys.map((key) => ({
            label: key,
            field: key,
            isToday: key === serverTodayField
        }))
    } catch (error) {
        const message = error?.response?.data?.message || 'Не удалось загрузить цены реализации'
        toast.add({ severity: 'error', summary: 'Ошибка загрузки', detail: message, life: 3500 })
        stores.value = []
        dateColumns.value = []
    }
}

const startEdit = (id) => {
  if (editingId.value !== null && editingId.value !== id) {
    const previousStore = stores.value.find((store) => store.id === editingId.value)
    const previousTodayField = dateColumns.value.find((column) => column.isToday)?.field

    if (previousStore && previousTodayField) {
      previousStore.prices[previousTodayField] = originalPrice.value
    }
  }

  editingId.value = id
  const store = stores.value.find(s => s.id === id)
  const todayField = dateColumns.value.find(c => c.isToday)?.field
  originalPrice.value = store.prices[todayField]
}

const cancelEdit = () => {
  const store = stores.value.find(s => s.id === editingId.value)
  const todayField = dateColumns.value.find(c => c.isToday)?.field
  if (store && todayField) store.prices[todayField] = originalPrice.value
  editingId.value = null
}

const normalizeEditablePrice = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null
    }

    const raw = String(value ?? '').trim()
    if (!raw) return null

    const normalized = raw.replace(/\s+/g, '').replace(',', '.')
    const parsed = Number(normalized)

    return Number.isFinite(parsed) ? parsed : null
}

const saveEdit = async (store) => {
    const todayField = dateColumns.value.find(c => c.isToday)?.field
    if (!todayField) {
        toast.add({ severity: 'warn', summary: 'Нет даты', detail: 'Не удалось определить текущую колонку цены реализации', life: 3000 })
        return
    }

    const newPrice = normalizeEditablePrice(store.prices[todayField])
    if (newPrice === null) {
        toast.add({ severity: 'warn', summary: 'Неверная цена', detail: 'Введите корректную цену реализации', life: 3000 })
        return
    }

    try {
        await axios.put(`/api/update-store-price/${store.id}/price`, {
            price: newPrice,
        })
        await fetchData()
        editingId.value = null
        originalPrice.value = null
        toast.add({ severity: 'success', summary: 'Сохранено', detail: 'Цена реализации обновлена', life: 2200 })
    } catch (error) {
        const validationMessage = error?.response?.data?.errors
            ? Object.values(error.response.data.errors).flat()[0]
            : null
        const message = validationMessage || error?.response?.data?.message || 'Не удалось сохранить цену реализации'
        toast.add({ severity: 'error', summary: 'Ошибка сохранения', detail: message, life: 3500 })
    }
}

onMounted(fetchData)
</script>

<style scoped>
:deep(.reference-table),
:deep(.reference-table .p-datatable-table-container) {
  height: 100%;
}

:deep(.reference-table .p-datatable-header) {
  border: 0;
  background: rgb(255 255 255);
  padding: 0;
}

:deep(.reference-table .p-datatable-thead > tr > th) {
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-weight: 600;
  border-color: rgb(226 232 240);
  padding: 0.875rem 1rem;
}

:deep(.reference-table .p-datatable-tbody > tr > td) {
  border-color: rgb(241 245 249);
  color: rgb(51 65 85);
  padding: 0.875rem 1rem;
}

:deep(.reference-table .p-datatable-tbody > tr:hover > td) {
  background: rgba(248, 250, 252, 0.95);
}

:deep(.reference-table .p-paginator) {
  border-top: 1px solid rgb(226 232 240);
  background: rgb(255 255 255);
  padding: 0.75rem 1rem;
}

:deep(.reference-table .p-paginator-page.p-paginator-page-selected) {
  background: rgb(16 185 129);
  color: rgb(255 255 255);
}

.p-datatable td {
  text-align: center;
}
</style>
